export const SCORE_THEME_STORAGE_KEY = 'museflow_score_theme';
export const LEGACY_PRACTICE_DARK_MODE_STORAGE_KEY = 'museflow_practice_dark_mode';

export const SCORE_THEMES = ['light', 'dimPaper', 'vsCode', 'dark'] as const;

export type ScoreTheme = (typeof SCORE_THEMES)[number];

export type ScoreThemePalette = {
  scoreBackground: string;
  scoreBackgroundPhaser: string;
  repertoireBackground: string;
  repertoireBackgroundPhaser: string;
  notationColor: string;
  notationColorNumber: number;
  staffLineColor: string;
  staffLineColorNumber: number;
  musicMarkingColor: string;
  footerTextColor: string;
  disabledNoteColor: string;
  selectedNoteHighlight: string;
  playbackCursor: string;
  playbackCursorNumber: number;
};

export const SCORE_THEME_LABELS: Record<ScoreTheme, string> = {
  light: 'Light',
  dimPaper: 'Dim Paper',
  vsCode: 'VS Code',
  dark: 'Dark',
};

export const SCORE_THEME_PALETTES: Record<ScoreTheme, ScoreThemePalette> = {
  light: {
    scoreBackground: '#FFFFFF',
    scoreBackgroundPhaser: '0xffffff',
    repertoireBackground: '#FFFFFF',
    repertoireBackgroundPhaser: '0xffffff',
    notationColor: '#000000',
    notationColorNumber: 0x000000,
    staffLineColor: '#000000',
    staffLineColorNumber: 0x000000,
    musicMarkingColor: '#000000',
    footerTextColor: '#888888',
    disabledNoteColor: 'rgba(0, 0, 0, 0.38)',
    selectedNoteHighlight: 'rgba(111, 135, 241, 0.24)',
    playbackCursor: 'rgba(79, 150, 189, 0.26)',
    playbackCursorNumber: 0x6f87f1,
  },
  dimPaper: {
    scoreBackground: '#EEDDC3',
    scoreBackgroundPhaser: '0xeeddc3',
    repertoireBackground: '#EEDDC3',
    repertoireBackgroundPhaser: '0xeeddc3',
    notationColor: '#211915',
    notationColorNumber: 0x211915,
    staffLineColor: '#382D25',
    staffLineColorNumber: 0x382d25,
    musicMarkingColor: '#2A201A',
    footerTextColor: '#6B5845',
    disabledNoteColor: 'rgba(33, 25, 21, 0.38)',
    selectedNoteHighlight: 'rgba(158, 113, 53, 0.28)',
    playbackCursor: 'rgba(92, 69, 42, 0.3)',
    playbackCursorNumber: 0x7a5b35,
  },
  vsCode: {
    scoreBackground: '#1E2736',
    scoreBackgroundPhaser: '0x1e2736',
    repertoireBackground: '#111827',
    repertoireBackgroundPhaser: '0x111827',
    notationColor: '#C5CAD3',
    notationColorNumber: 0xc5cad3,
    staffLineColor: '#5D708D',
    staffLineColorNumber: 0x5d708d,
    musicMarkingColor: '#AEB8C7',
    footerTextColor: '#8392A8',
    disabledNoteColor: 'rgba(197, 202, 211, 0.34)',
    selectedNoteHighlight: 'rgba(0, 122, 204, 0.28)',
    playbackCursor: 'rgba(0, 122, 204, 0.28)',
    playbackCursorNumber: 0x007acc,
  },
  dark: {
    scoreBackground: '#050B13',
    scoreBackgroundPhaser: '0x050b13',
    repertoireBackground: '#050B13',
    repertoireBackgroundPhaser: '0x050b13',
    notationColor: '#F3F7FF',
    notationColorNumber: 0xf3f7ff,
    staffLineColor: '#F3F7FF',
    staffLineColorNumber: 0xf3f7ff,
    musicMarkingColor: '#CBD5E1',
    footerTextColor: '#8795AA',
    disabledNoteColor: 'rgba(243, 247, 255, 0.38)',
    selectedNoteHighlight: 'rgba(255, 214, 102, 0.32)',
    playbackCursor: 'rgba(79, 150, 189, 0.26)',
    playbackCursorNumber: 0x6f87f1,
  },
};

export const isScoreTheme = (value: unknown): value is ScoreTheme =>
  typeof value === 'string' && SCORE_THEMES.includes(value as ScoreTheme);

export const normalizeScoreTheme = (
  value?: string | null,
  legacyPracticeDarkMode?: boolean | null,
): ScoreTheme => {
  if (isScoreTheme(value)) return value;
  return legacyPracticeDarkMode ? 'dark' : 'light';
};

export const scoreThemeToPracticeDarkMode = (scoreTheme: ScoreTheme) =>
  scoreTheme === 'vsCode' || scoreTheme === 'dark';

export const getScoreThemePalette = (scoreTheme: ScoreTheme) => SCORE_THEME_PALETTES[scoreTheme];

export const readScoreThemePreference = (): ScoreTheme | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;

    const stored = window.localStorage.getItem(SCORE_THEME_STORAGE_KEY);
    if (isScoreTheme(stored)) return stored;

    const legacyStored = window.localStorage.getItem(LEGACY_PRACTICE_DARK_MODE_STORAGE_KEY);
    if (legacyStored === 'true') return 'dark';
    if (legacyStored === 'false') return 'light';
  } catch {
    // localStorage may be unavailable in private browsing or native webviews.
  }
  return undefined;
};

export const writeScoreThemePreference = (scoreTheme: ScoreTheme) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SCORE_THEME_STORAGE_KEY, scoreTheme);
    window.localStorage.setItem(
      LEGACY_PRACTICE_DARK_MODE_STORAGE_KEY,
      String(scoreThemeToPracticeDarkMode(scoreTheme)),
    );
  } catch {
    // localStorage persistence is best-effort; Redux/backend still carry the setting.
  }
};

export const readPracticeDarkModePreference = (): boolean | undefined => {
  const scoreTheme = readScoreThemePreference();
  return scoreTheme === undefined ? undefined : scoreThemeToPracticeDarkMode(scoreTheme);
};

export const writePracticeDarkModePreference = (enabled: boolean) => {
  writeScoreThemePreference(enabled ? 'dark' : 'light');
};
